import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@material-ui/core';

import Alert from '@material-ui/lab/Alert';

import PropTypes from 'prop-types';

import BasicButton from '../buttons/basicButton';
import CustomCheckBox from '../fields/customCheckBox';
import SpinnerLoading from '../feedback/spinnerLoading';

import { addEnrolmentTeamMembers, getProgramInfo } from '../../utils/comms';

import translations from '../../languages/i18n';

import { customOptionsTableStyles } from '../../styles/formElementsStyles';

import { SessionContext } from '../../utils/context';

export default function AddMembersDialog({ currentMembers, handleClose, handleFeedback, idEnrolment, idProgram, open, title }) {
  const sessionContext = useContext(SessionContext);
  const bgColors = customOptionsTableStyles();

  const [emptySelectionWarning, setEmptySelectionWarning] = useState(false);
  const [ready, setReady] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showLoading, setShowLoading] = useState(false);
  const [programAccessibleBy, setProgramAccessibleBy] = useState([]);

  useEffect(() => {
    const getProgramTeamFromServer = async () => {
      setShowLoading(true);
      const response = await getProgramInfo(idProgram, sessionContext.userToken);
      setShowLoading(false);
      if (response.status === 200) { // Tenim llistat
        if (currentMembers.length !== 0) { // Només hem de mostrar els clínics que no són de l'equip de l'enrolment
          const accessibleByClinicians = response.data.program.accessibleBy.clinicians;
          // Del llistat de clínics del programa rebut, cercam els ids dels que ja són dins currentMembers
          const idsToRemove = currentMembers.map((member) => {
            const foundClinician = accessibleByClinicians.find((clinician) => clinician.clinician !== null && clinician.clinician._id === member.clinician._id);
            // Per evitar error en cas de que el clínic estigui a l'equip clínic de la subscripció (dins currentMembers)
            // però hagi deixat d'estar associat al programa de la subscripció (no està dins accessibleByClinicians)
            // abans de retornar l'id del clínic comprovam que foundClinician existeixi. Si no eixiteix retornam "undefined"
            return foundClinician && foundClinician.clinician._id;
          });
          // Esborram del llistat de clínics del programa rebut (el llistat que mostrarem a aquest component) els ids dels clínics que ja formen part de l'equip
          idsToRemove.map((id) => {
            const idx = accessibleByClinicians.findIndex((clinician) => clinician.clinician._id === id);
            // En cas de tenir un "undefined" als idsToRemove idx en aquest cas valdrà -1
            if (idx >= 0) accessibleByClinicians.splice(idx, 1);
          });
          setProgramAccessibleBy(accessibleByClinicians);
        } else { // No hi ha cap membre a l'equip, mostram tots els clínics del programa
          setProgramAccessibleBy(response.data.program.accessibleBy ? response.data.program.accessibleBy.clinicians : []);
        }
        setReady(true);
      } else if (response === 400) { // Error amb les dades
        console.log('Error 400');
      } else { // Login KO. Altres errors
        console.log(response);
      }
    };
    getProgramTeamFromServer();
  }, []); // [] Indica que l'useEffect només s'executa una vegada, no a cada render

  const addMembers = async () => {
    if (selectedMembers.length === 0) { // Si no hi ha cap clinic seleccionat avisam
      setEmptySelectionWarning(true);
    } else {
      setShowLoading(true);
      const response = await addEnrolmentTeamMembers(selectedMembers, idEnrolment, sessionContext.userToken);
      setShowLoading(false);
      if (response.status === 200) {
        handleClose();
        handleFeedback('success', 'AddClinicianToEnrolmentTeam', true);
      } else if (response === 'timeOut') { // El servidor està caigut o l'usuari no té connexió
        handleFeedback('error', 'TimeOut');
      } else { // Error amb les dades o al servidor
        handleFeedback('error', 'AddClinicianToEnrolmentTeam', false);
      }
    }
  };

  const closeDialog = () => {
    handleClose();
  };

  const handleCheck = (id) => {
    setEmptySelectionWarning(false); // Per si un cas, ocultam el warning
    const selectedMembersCopy = selectedMembers.slice();
    // Cercam si dins selectedMembersCopy hi ha el valor de clinic
    const idx = selectedMembersCopy.findIndex((item) => item.clinician === id);
    // Si idx és -1 vol dir que no ha trobat el valor, l'afegim a la taula
    // Si no hi ha cap membre a l'equip, posam com admin al primer que es selecciona
    if (idx < 0) selectedMembersCopy.push({ clinician: id, admin: (currentMembers.length === 0 && selectedMembers.length === 0) || false });
    else selectedMembersCopy.splice(idx, 1); // L'ha trobat, l'eliminam de la taula
    setSelectedMembers(selectedMembersCopy);
  };

  const listOfMembers = (programAccessibleByData) => {
    return (
      <TableContainer component={Paper}>
        <Table aria-label='members list table'>
          <TableBody>
            <TableRow className={bgColors.tableRowHeaderBgColor}>
              {/* ATENCIÓ: no pintam la columna _id */}
              <TableCell align='left'><strong>{translations['_username']}</strong></TableCell>
              <TableCell align='left' />
            </TableRow>
            {(programAccessibleByData.length !== 0 && (
              programAccessibleByData.map((data, index) => (
                <TableRow key={data.clinician._id} className={index % 2 ? bgColors.tableRowBgColor1 : bgColors.tableRowBgColor2} hover>
                  {/* ATENCIÓ: no pintam la columna _id */}
                  <TableCell align='left'>{data.clinician.username}</TableCell>
                  <TableCell align='center'>
                    <CustomCheckBox
                      checked={selectedMembers.findIndex((item) => item.clinician === data.clinician._id) > -1} // Si res.value es dins la taula la condició dona true
                      onClick={() => handleCheck(data.clinician._id)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )) || (
              <TableRow>
                <TableCell align='center' colSpan='2'>
                  <Typography variant='h6' component='div'>{translations['_emptyAccessibleBy']}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <div>
      <Dialog
        open={open}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle id='alert-dialog-title'>{title}</DialogTitle>
        <DialogContent>
          <SpinnerLoading show={showLoading} />
          <Grid container spacing={2} justify='space-around' direction='column'>
            <Grid item xs={12}>
              { ready && listOfMembers(programAccessibleBy) }
            </Grid>
            {emptySelectionWarning && (
            <Grid item xs={12}>
              <Alert severity='error'>{translations['_emptyMembersSelection']}</Alert>
            </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <BasicButton handleClick={addMembers} type='accept' /> {/* Per enviar el formulari des d'un botó meu, sinó s'ha d'emprar un Button type='submit' */}
          <BasicButton handleClick={closeDialog} type='cancel' />
        </DialogActions>
      </Dialog>
    </div>
  );
}

// Indica i controla els props que se han de passar al component
AddMembersDialog.propTypes = {
  currentMembers: PropTypes.array.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleFeedback: PropTypes.func.isRequired,
  idEnrolment: PropTypes.string.isRequired,
  idProgram: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
};
